import React, { useState, useEffect } from 'react';
import { DollarSign, History, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { Ingredient, SupplierIngredient, PriceHistory } from '@/types/ingredient.types';
import { getActiveSuppliers, getCurrentPrice } from '@/types/ingredient.types';
import { ingredientsService, PriceRecord } from '@/services/ingredients.service';

interface PriceHistoryModalProps {
  isOpen: boolean;
  ingredient: Ingredient | null;
  onClose: () => void;
  onSavePrice: (price: number, effectiveDate: string, reason?: string) => void;
}

export const PriceHistoryModal: React.FC<PriceHistoryModalProps> = ({
  isOpen,
  ingredient,
  onClose,
  onSavePrice
}) => {
  const [selectedSupplier, setSelectedSupplier] = useState<SupplierIngredient | null>(null);
  const [newPrice, setNewPrice] = useState(0);
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [priceHistory, setPriceHistory] = useState<PriceHistory[]>([]);
  const [loading, setLoading] = useState(false);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN'
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('es-PE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  useEffect(() => {
    if (ingredient) {
      // Get active suppliers and select preferred one
      const activeSuppliers = getActiveSuppliers(ingredient);
      const preferredSupplier = activeSuppliers.find(s => s.isPreferred) || activeSuppliers[0] || null;

      setSelectedSupplier(preferredSupplier);

      if (preferredSupplier) {
        const currentPrice = getCurrentPrice(preferredSupplier, ingredient.priceHistory);
        setNewPrice(currentPrice);
      } else {
        setNewPrice(ingredient.globalCostPerUnit || 0);
      }

      // Set price history from ingredient data
      setPriceHistory(ingredient.priceHistory || []);
    }
  }, [ingredient]);

  const handleSupplierChange = (supplierId: string) => {
    if (!ingredient) return;

    const activeSuppliers = getActiveSuppliers(ingredient);
    const supplier = activeSuppliers.find(s => s.supplierId === supplierId);

    if (supplier) {
      setSelectedSupplier(supplier);
      const currentPrice = getCurrentPrice(supplier, ingredient.priceHistory);
      setNewPrice(currentPrice);
    }
  };

  // Get filtered price history for selected supplier
  const getFilteredPriceHistory = (): PriceHistory[] => {
    if (!selectedSupplier) return [];
    return priceHistory.filter(p => p.supplierId === selectedSupplier.supplierId);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPrice || newPrice <= 0 || !ingredient) {
      return;
    }

    setLoading(true);
    try {
      // Call the real API to update price
      const response = await ingredientsService.updatePrice(
        ingredient.id,
        newPrice,
        effectiveDate,
        reason,
        selectedSupplier?.supplierId
      );

      if (response.success) {
        // Call the onSavePrice callback to refresh the parent component
        await onSavePrice(newPrice, effectiveDate, reason);
        
        // Reload the price history to show the new record
        await loadPriceHistory();
        
        // Reset form
        setReason('');
        onClose();
      }
    } catch (error) {
      console.error('Error updating price:', error);
      // You might want to show an error toast here
    } finally {
      setLoading(false);
    }
  };

  const currentPrice = selectedSupplier
    ? getCurrentPrice(selectedSupplier, ingredient?.priceHistory)
    : ingredient?.globalCostPerUnit || 0;
  const priceChange = newPrice - currentPrice;
  const priceChangePercent = currentPrice > 0 ? (priceChange / currentPrice) * 100 : 0;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[90vw] max-h-[85vh] w-[90vw] min-w-[800px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-macartur-primary" />
            Gestión de Precios - {ingredient?.name}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* New Price Form */}
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">Precio Actual</h3>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(currentPrice)}
              </div>
              <div className="text-sm text-gray-500">
                por {ingredient?.unit}
              </div>
              {selectedSupplier && (
                <div className="mt-2 text-sm text-blue-600">
                  {selectedSupplier.supplier?.businessName || `Proveedor ID: ${selectedSupplier.supplierId}`}
                  {selectedSupplier.isPreferred && (
                    <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                      ⭐ Preferido
                    </span>
                  )}
                </div>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="supplier">Proveedor</Label>
                <Select
                  value={selectedSupplier?.supplierId || ''}
                  onValueChange={handleSupplierChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar proveedor" />
                  </SelectTrigger>
                  <SelectContent>
                    {ingredient && getActiveSuppliers(ingredient).map((supplier) => (
                      <SelectItem key={supplier.supplierId} value={supplier.supplierId}>
                        <div className="flex items-center gap-2">
                          <span>{supplier.supplier?.businessName || `Proveedor ${supplier.supplierId}`}</span>
                          {supplier.isPreferred && (
                            <span className="text-xs text-yellow-600">⭐</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!ingredient || getActiveSuppliers(ingredient).length === 0 && (
                  <div className="text-xs text-red-600 mt-1">
                    No hay proveedores activos para este ingrediente
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="newPrice">Nuevo Precio *</Label>
                <Input
                  id="newPrice"
                  type="number"
                  min="0"
                  step="0.01"
                  value={newPrice}
                  onChange={(e) => setNewPrice(parseFloat(e.target.value) || 0)}
                  required
                />
                {priceChange !== 0 && (
                  <div className="mt-2 text-sm">
                    <span className={priceChange > 0 ? 'text-red-600' : 'text-green-600'}>
                      {priceChange > 0 ? '+' : ''}{formatCurrency(priceChange)} 
                      ({priceChangePercent > 0 ? '+' : ''}{priceChangePercent.toFixed(1)}%)
                    </span>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="effectiveDate">Fecha Inicio *</Label>
                <Input
                  id="effectiveDate"
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  required
                />
                <div className="text-xs text-gray-500 mt-1">
                  Fecha desde cuando será efectivo el nuevo precio
                </div>
              </div>

              <div>
                <Label htmlFor="endDate">Fecha Fin</Label>
                <Input
                  id="endDate"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
                <div className="text-xs text-gray-500 mt-1">
                  Fecha hasta cuando estará vigente (opcional)
                </div>
              </div>

              <div>
                <Label htmlFor="reason">Motivo del Cambio</Label>
                <Input
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Ej: Actualización de proveedor, inflación, etc."
                />
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={onClose}
                  disabled={loading}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={loading || !newPrice || newPrice <= 0}
                  className="bg-macartur-primary hover:bg-macartur-primary/90"
                >
                  {loading ? 'Guardando...' : 'Actualizar Precio'}
                </Button>
              </div>
            </form>
          </div>

          {/* Price History */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              <History className="w-4 h-4 text-gray-500" />
              <h3 className="font-medium text-gray-900">Historial de Precios</h3>
              {selectedSupplier && (
                <span className="text-xs text-gray-500">
                  - {selectedSupplier.supplier?.businessName || `Proveedor ${selectedSupplier.supplierId}`}
                </span>
              )}
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto">
              {getFilteredPriceHistory().map((record, index) => (
                <div key={record.id} className="border rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div className="font-medium text-gray-900">
                      {formatCurrency(record.price)}
                    </div>
                    {index === 0 && (
                      <Badge variant="outline" className="text-xs">
                        Actual
                      </Badge>
                    )}
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar className="w-3 h-3" />
                      Creado: {formatDate(record.createdAt)}
                    </div>
                    <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                      <Calendar className="w-3 h-3" />
                      Vigente desde: {new Date(record.effectiveDate).toLocaleDateString('es-PE')}
                    </div>
                    {record.endDate && (
                      <div className="flex items-center gap-2 text-sm text-orange-600">
                        <Calendar className="w-3 h-3" />
                        Vigente hasta: {new Date(record.endDate).toLocaleDateString('es-PE')}
                      </div>
                    )}
                  </div>
                  
                  {record.reason && (
                    <div className="text-sm text-gray-600">
                      {record.reason}
                    </div>
                  )}
                  
                  {index > 0 && (
                    <div className="text-xs text-gray-400 mt-1">
                      Cambio: {((record.price - getFilteredPriceHistory()[index - 1].price) / getFilteredPriceHistory()[index - 1].price * 100).toFixed(1)}%
                    </div>
                  )}
                </div>
              ))}

              {getFilteredPriceHistory().length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <History className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                  <p>
                    {selectedSupplier
                      ? `No hay historial de precios para ${selectedSupplier.supplier?.businessName || 'este proveedor'}`
                      : 'Selecciona un proveedor para ver el historial'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};